/**
 * Creator onboarding on the player app (D165b) — the trigger and the
 * return, behind `?creator=…`, until the studio exists.
 *
 * @remarks
 * Deletable by design. The store's doors (`POST /creators/onboard`,
 * `GET /creators/me`) are the product; this file is the one page in front
 * of them today. `?creator=onboard` (or Stripe's own `refresh`, sent when a
 * link expired) asks the store for Stripe's page and goes there;
 * `?creator=returned` asks the store what state the account is in and says
 * so. Nothing here is reachable from a button — a player never meets it.
 * Moving onboarding to `studio.orpheusquest.com` is deleting this file and
 * the one arrival read in the app component.
 *
 * @packageDocumentation
 */

import { useEffect, useState } from 'react';

import { CREATOR_ONBOARDING, onboardingRefusedLine } from './creator-copy.js';
import { type CreatorArrival, type StoreClient } from './store-client.js';

/** Props for {@link CreatorOnboarding}. */
export interface CreatorOnboardingProps {
	readonly arrival: CreatorArrival;
	/** Where to send the browser; `window.location.assign` in the app, a stand-in in a test. */
	readonly navigate: (url: string) => void;
	/** The page Stripe sends the browser back to — this one, query and all. */
	readonly returnUrl: string;
	readonly storefront: StoreClient;
}

/**
 * Act on the arrival and say what happened.
 *
 * @param props - {@link CreatorOnboardingProps}
 * @returns One status line
 */
export const CreatorOnboarding = ({ arrival, navigate, returnUrl, storefront }: CreatorOnboardingProps) => {
	const [line, setLine] = useState<string>(arrival === 'returned' ? '' : CREATOR_ONBOARDING.starting);

	useEffect(() => {
		let isDisposed = false;

		if (arrival === 'returned') {
			void storefront.creator().then((answer) => {
				if (isDisposed) {
					return;
				}

				if (answer !== null && 'refusal' in answer) {
					setLine(onboardingRefusedLine(answer.refusal));
				} else {
					setLine(answer?.isPayable === true ? CREATOR_ONBOARDING.payable : CREATOR_ONBOARDING.notYetPayable);
				}
			});
		} else {
			void storefront.startOnboarding(returnUrl).then((outcome) => {
				if (isDisposed) {
					return;
				}

				if (outcome.kind === 'started') {
					navigate(outcome.url);
				} else {
					setLine(onboardingRefusedLine(outcome.refusal));
				}
			});
		}

		return () => {
			isDisposed = true;
		};
	}, [arrival, navigate, returnUrl, storefront]);

	return line === '' ? undefined : (
		<p className="session-view__purchase-arrival" role="status">
			{line}
		</p>
	);
};
